import {useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayContentPadding,
  usePlayOptionTextStyle,
  usePlaySelectionMode,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {SLIDE_TYPE_META} from '#/types/capsule'

export function TrueFalseSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const p = useFlashBookPlayPalette()
  // The book page supplies its own margins; capsule play keeps its 24px.
  const padding = usePlayContentPadding(24)
  // The answer wears the book's own hand, not the app's UI font.
  const answerText = usePlayOptionTextStyle()
  // A whole number of the page's rules between the question and the answer.
  const stackGap = usePlayStackGap(16)
  // Where the theme marks with a highlighter, the two answers are words
  // written on the page rather than a pair of tinted panels: the chosen one is
  // struck, the other is left as the writing it is.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()
  const [selected, setSelected] = useState<'true' | 'false' | null>(null)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)
  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelected(null)
  }

  const solutionText = slide.answer === 'true' ? 'True' : 'False'

  const getBtnColors = (val: 'true' | 'false') => {
    const clr = val === 'true' ? p.positive : p.negative
    const isSelected = selected === val
    if (highlight) return {bg: 'transparent', border: 'transparent'}
    if (showFeedback) {
      if (val === slide.answer) return {bg: clr + '33', border: clr}
      if (isSelected) return {bg: p.negative + '33', border: p.negative}
      return {bg: clr + '12', border: clr + '44'}
    }
    if (isSelected) return {bg: clr + '33', border: clr}
    return {bg: clr + '12', border: clr + '44'}
  }

  const getIconColor = (val: 'true' | 'false') => {
    const clr = val === 'true' ? p.positive : p.negative
    if (showFeedback && val === slide.answer) return clr
    if (showFeedback && selected === val) return p.negative
    if (selected === val) return clr
    return clr + '88'
  }

  /** The ink the answer's own word wears, or nothing when it is unmarked. */
  const markFor = (val: 'true' | 'false') => {
    const clr = val === 'true' ? p.positive : p.negative
    if (showFeedback) {
      if (val === slide.answer) return clr
      return selected === val ? p.negative : null
    }
    return selected === val ? clr : null
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={{padding, gap: stackGap}}>
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>

        <View style={ss.btnRow}>
          <TouchableOpacity
            style={[
              ss.tfBtn,
              {
                backgroundColor: getBtnColors('true').bg,
                borderColor: getBtnColors('true').border,
              },
            ]}
            onPress={() => {
              if (showFeedback) return
              setSelected('true')
              const isCorrect = slide.answer === 'true'
              onAnswer({
                isCorrect,
                points: isCorrect ? 1 : 0,
                maxPoints: 1,
                solutionText,
                explanation: slide.explanation,
                userAnswer: 'true',
              })
            }}
            accessibilityRole="button"
            accessibilityLabel={l`True`}
            accessibilityHint={undefined}>
            <Feather name="check" size={32} color={getIconColor('true')} />
            <Text style={[ss.tfBtnText, answerText, {color: p.body}]}>
              <Text style={ink(markFor('true'))}>
                <Trans>True</Trans>
              </Text>
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              ss.tfBtn,
              {
                backgroundColor: getBtnColors('false').bg,
                borderColor: getBtnColors('false').border,
              },
            ]}
            onPress={() => {
              if (showFeedback) return
              setSelected('false')
              const isCorrect = slide.answer === 'false'
              onAnswer({
                isCorrect,
                points: isCorrect ? 1 : 0,
                maxPoints: 1,
                solutionText,
                explanation: slide.explanation,
                userAnswer: 'false',
              })
            }}
            accessibilityRole="button"
            accessibilityLabel={l`False`}
            accessibilityHint={undefined}>
            <Feather name="x" size={32} color={getIconColor('false')} />
            <Text style={[ss.tfBtnText, answerText, {color: p.body}]}>
              <Text style={ink(markFor('false'))}>
                <Trans>False</Trans>
              </Text>
            </Text>
          </TouchableOpacity>
        </View>
      </PlayScroller>
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  btnRow: {flexDirection: 'row', gap: 14, marginTop: 8},
  tfBtn: {
    flex: 1,
    borderWidth: 1.5,
    borderRadius: 14,
    paddingVertical: 24,
    alignItems: 'center',
    gap: 8,
  },
  tfBtnText: {fontSize: 18, fontWeight: '700', lineHeight: 24},
})
